<template>
  <div v-if="activeSplit" class="summary-page">
    <div class="fixed-background">
      <div class="ambient-light main-spot" :style="{ background: splitColor }"></div>
      <div class="ambient-light secondary-spot" :style="{ background: splitColor }"></div>
      <div class="noise-texture"></div>
    </div>

    <div class="glass-header">
      <v-btn icon large color="white" @click="leaveSummary" class="back-btn" :disabled="isSaving">
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>
      <div class="header-content">
        <span class="header-title">ZUSAMMENFASSUNG</span>
        <span class="header-date">{{ currentDate }}</span>
      </div>
      <div style="width: 44px"></div>
    </div>

    <div class="scroll-content">
      <section v-if="liveSession && liveSession.recoveryReview" class="summary-recovery" aria-label="Trainingsangaben prüfen" aria-live="polite">
        <span class="summary-recovery-icon"><v-icon color="#8ce7bb" size="23">{{ liveSession.timeReviewRequired ? 'mdi-clock-check-outline' : 'mdi-check' }}</v-icon></span>
        <div class="summary-recovery-copy">
          <strong>{{ sessionSaved ? 'Training gespeichert' : liveSession.pendingSave ? 'Speicherung noch offen' : liveSession.timeReviewRequired ? 'Kurz prüfen, dann speichern' : 'Bereit zum Speichern' }}</strong>
          <span>{{ currentDate }} · {{ totalSets }} {{ totalSets === 1 ? 'Satz' : 'Sätze' }} erfasst</span>
          <span>{{ liveSession.timeReviewRequired ? 'Bitte bestätige Datum und Trainingsdauer.' : durationString + ' · Angaben bestätigt' }}</span>
        </div>
        <v-btn v-if="!saveLocked" text class="summary-recovery-action" @click="openDurationDialog">
          {{ liveSession.timeReviewRequired ? 'Prüfen' : 'Ändern' }}<v-icon right size="17">mdi-arrow-right</v-icon>
        </v-btn>
      </section>
      <div v-if="isHyroxSummary" class="hyrox-container pt-8">
        <div class="text-center mb-8">
          <h1 class="hyrox-title" :style="{ color: splitColor }">RACE FINISHED</h1>
          <div class="hyrox-time">{{ durationClock }}</div>
          <div v-if="!durationNeedsInput" class="caption grey--text mb-3">Std. : Min. : Sek.</div>
          <p class="white--text opacity-70">{{ activeSplit.title }}</p>
        </div>

        <v-list dark color="transparent" class="pa-0">
          <template v-for="(segment, index) in hyroxSegments">
            <v-list-item :key="index" class="glass-list-item mb-2 mx-4" @click="toggleSegment(index)">
              <v-list-item-avatar tile size="44" class="rounded-lg">
                <v-img :src="`/img/uebungen/${segment.icon.replace(/\.[^/.]+$/, '')}.webp`"></v-img>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="font-weight-bold white--text">{{ segment.title }}</v-list-item-title>
              </v-list-item-content>
              <v-list-item-action class="text-right">
                <div class="font-weight-bold white--text text-h6">{{ segment.time }}</div>
                <div v-if="segment.comparison" class="caption font-weight-bold" :class="segment.comparisonClass">
                  {{ segment.comparison }}
                </div>
              </v-list-item-action>
              <v-icon small color="grey lighten-1" :class="{ 'rotate-180': expandedSegmentIndex === index }">mdi-chevron-down</v-icon>
            </v-list-item>

            <v-expand-transition :key="`expand-${index}`">
              <div v-if="expandedSegmentIndex === index" class="mx-4 mb-4 px-4 py-2 history-bg rounded-lg">
                <div class="caption grey--text mb-2">Verlauf:</div>
                <div v-for="(item, i) in getSegmentHistory(segment)" :key="i" class="d-flex justify-space-between text-body-2 mb-1">
                  <span class="white--text">{{ item.date }}</span>
                  <span class="font-weight-bold" :style="{ color: splitColor }">{{ item.time }}</span>
                </div>
              </div>
            </v-expand-transition>
          </template>
        </v-list>
      </div>

      <template v-else>
        <v-container class="pt-6 pb-0">
          <div class="text-center mb-8">
            <div class="hero-avatar mb-4" :style="{ boxShadow: `0 0 40px ${splitColor}55`, borderColor: splitColor }">
              <v-img :src="splitImage" height="100%" width="100%">
                <template v-slot:placeholder>
                  <v-sheet color="grey darken-3" class="fill-height d-flex align-center justify-center">
                    <v-icon x-large color="grey">mdi-dumbbell</v-icon>
                  </v-sheet>
                </template>
              </v-img>
            </div>

            <h2 class="text-h4 font-weight-black white--text text-uppercase mb-2 shadow-text">
              {{ splitTitle }}
            </h2>

            <div class="d-flex justify-center flex-wrap" style="gap: 8px">
              <v-chip
                v-for="tag in focusTags"
                :key="tag"
                outlined
                :color="splitColor"
                class="font-weight-bold px-4"
                style="border-width: 2px; background: rgba(0, 0, 0, 0.4)">
                {{ tag }}
              </v-chip>
            </div>
          </div>

          <div v-if="newPersonalRecords.length > 0" class="mb-8">
            <div class="section-title mb-3 pl-1">
              <v-icon color="#FFD700" size="20" class="mr-2">mdi-trophy</v-icon>
              NEUE REKORDE
            </div>
            <v-slide-group show-arrows="desktop" class="pr-slider">
              <v-slide-item v-for="(pr, i) in newPersonalRecords" :key="i">
                <div class="glass-card pr-card mr-3 pa-4">
                  <div class="pr-glow"></div>
                  <div class="font-weight-bold white--text text-truncate mb-1">{{ pr.exerciseTitle }}</div>
                  <div class="caption grey--text mb-3">{{ pr.type }}</div>
                  <div class="d-flex align-center justify-space-between">
                    <span class="grey--text text-decoration-line-through caption">{{ pr.oldValue }}</span>
                    <v-icon x-small color="white">mdi-arrow-right</v-icon>
                    <span class="font-weight-black text-h6" :style="{ color: '#00E676' }">{{ pr.newValue }}</span>
                  </div>
                </div>
              </v-slide-item>
            </v-slide-group>
          </div>

          <div class="stats-grid mb-8">
            <div
              class="glass-card stat-hero pa-5 relative overflow-hidden d-flex align-center justify-space-between ripple-effect"
              :style="{ borderTop: `4px solid ${splitColor}` }"
              @click="nextFunFact">
              <div class="glow-bg" :style="{ background: `radial-gradient(circle, ${currentFunFact.color}33 0%, transparent 70%)` }"></div>

              <div style="position: relative; z-index: 2; flex-grow: 1">
                <div class="d-flex align-center justify-space-between mb-1">
                  <div class="caption font-weight-bold grey--text text--lighten-1 text-uppercase">Bewegte Last</div>

                  <div class="tap-hint-badge">
                    <v-icon x-small color="white" class="mr-1" style="opacity: 0.9">mdi-cursor-default-click</v-icon>
                    TIPPEN
                  </div>
                </div>

                <v-fade-transition mode="out-in">
                  <div :key="currentFunFact.label" class="d-flex align-baseline flex-wrap mt-1">
                    <span class="text-h3 font-weight-black white--text mr-2" style="line-height: 1">
                      {{ currentFunFact.count }}
                    </span>
                    <span class="text-h6 font-weight-bold text-truncate" :style="{ color: currentFunFact.color }">
                      {{ currentFunFact.label }}
                    </span>
                  </div>
                </v-fade-transition>

                <div class="caption grey--text mt-2 d-flex align-center">
                  <v-icon x-small color="grey" class="mr-1">mdi-weight-kilogram</v-icon>
                  <span>{{ animatedVolume.toLocaleString("de-DE") }} kg total</span>
                </div>
              </div>

              <v-fade-transition mode="out-in">
                <div
                  :key="currentFunFact.icon"
                  class="fun-fact-icon-wrapper ml-3"
                  :style="{ borderColor: currentFunFact.color, boxShadow: `0 0 20px ${currentFunFact.color}20` }">
                  <v-icon size="40" :color="currentFunFact.color" class="fun-fact-icon">
                    {{ currentFunFact.icon }}
                  </v-icon>
                </div>
              </v-fade-transition>
            </div>
            <button type="button" class="glass-card duration-stat pa-4 d-flex flex-column justify-center text-center relative ripple-effect" :disabled="saveLocked" aria-label="Trainingsdauer und Datum anpassen" @click="openDurationDialog">
              <span v-if="!saveLocked" class="edit-circle" :style="{ background: splitColor }">
                <v-icon x-small color="black">mdi-pencil</v-icon>
              </span>
              <span class="caption grey--text text-uppercase mb-1">Dauer</span>
              <span class="duration-stat-value">{{ durationString }}</span>
              <span v-if="!saveLocked" class="duration-stat-hint">{{ durationNeedsInput ? 'Tatsächliche Dauer eintragen' : liveSession && liveSession.timeReviewRequired ? 'Vorschlag · bitte prüfen' : 'Anpassen' }}</span>
            </button>

            <div class="glass-card pa-4 relative ripple-effect" @click="!saveLocked && (showCaloriesDialog = true)">
              <div class="edit-circle" :style="{ background: splitColor }">
                <v-icon x-small color="black">mdi-pencil</v-icon>
              </div>
              <div class="caption grey--text text-uppercase mb-1">Kalorien</div>
              <div class="d-flex align-baseline justify-center">
                <span class="text-h5 font-weight-bold white--text mr-1">{{ displayCalories }}</span>
                <span class="caption grey--text">kcal</span>
              </div>
            </div>

            <div class="glass-card pa-4 text-center">
              <div class="text-h4 font-weight-bold white--text mb-1">{{ animatedSets }}</div>
              <div class="caption grey--text text-uppercase">Sätze</div>
            </div>

            <div class="glass-card pa-4 text-center">
              <div class="text-h4 font-weight-bold white--text mb-1">{{ animatedReps }}</div>
              <div class="caption grey--text text-uppercase">Reps</div>
            </div>

            <div v-if="animatedDistance > 0" class="glass-card full-width pa-4 d-flex align-center justify-space-between">
              <div>
                <div class="caption grey--text text-uppercase">Distanz</div>
                <span class="text-h5 font-weight-bold white--text">{{ animatedDistance }} m</span>
              </div>
              <v-icon color="white" style="opacity: 0.3" large>mdi-map-marker-distance</v-icon>
            </div>
          </div>

        </v-container>
      </template>
      <div class="health-summary-section px-4">
        <AppleHealthWorkoutLink
          v-if="canUseWatchFeature"
          ref="healthImport"
          v-model="selectedHealthWorkout"
          :execution="healthImportExecution"
          :pending="true"
          :disabled="saveLocked"
          @available="healthImportAvailable = $event"
          :direct-watch="Boolean(watchCompanion.clientSessionId)" />
        <div v-if="!isHyroxSummary" class="mt-8 mb-4">
          <div class="section-title mb-2 pl-1">Hilf uns, besser zu werden!</div>
          <v-textarea v-model="trainingFeedback" class="bright-input" :disabled="saveLocked"
            placeholder="Was können wir an der App verbessern? Dein Feedback geht direkt an uns Entwickler (optional)."
            rows="3" auto-grow solo flat hide-details />
        </div>
      </div>
    </div>

    <v-footer fixed class="transparent-footer pa-4">
      <div class="footer-blur"></div>
      <button v-if="canUseWatchFeature && healthImportAvailable && !watchCompanion.clientSessionId" type="button" class="health-save-shortcut"
        :disabled="saveLocked" @click="openHealthImport">
        <v-icon size="16" :color="selectedHealthWorkout ? 'primary' : '#ff91a5'">{{ selectedHealthWorkout ? 'mdi-check-circle' : 'mdi-heart' }}</v-icon>
        {{ selectedHealthWorkout ? 'Gesundheitsdaten ausgewählt' : 'Gesundheitsdaten hinzufügen' }}
        <v-icon size="16" color="#b7c7bf">mdi-chevron-right</v-icon>
      </button>
      <v-btn
        x-large
        block
        rounded
        class="save-btn font-weight-bold"
        :style="{ background: splitColor, color: isLightColor(splitColor) ? 'black' : 'white' }"
        @click="saveSessionAndExit"
        :loading="isSaving"
        elevation="10">
        {{ sessionSaved ? "ZUR TRAININGSÜBERSICHT" : "TRAINING SPEICHERN" }}
        <v-icon right>{{ sessionSaved ? "mdi-arrow-right" : "mdi-content-save-check" }}</v-icon>
        <template v-slot:loader>
          <v-progress-circular indeterminate size="20" width="2" class="mr-2" />
          {{ sessionSaved ? "Training gespeichert" : "Training wird gespeichert" }}
        </template>
      </v-btn>
    </v-footer>

    <v-dialog v-model="showCaloriesDialog" max-width="320" content-class="dialog-card">
      <v-card class="glass-modal pa-5 text-center">
        <h3 class="white--text text-h6 font-weight-bold mb-4">Kalorien anpassen</h3>
        <div class="input-display mb-6">
          <input v-model.number="manualCalories" type="number" class="big-input" placeholder="0" autofocus />
          <span class="unit">kcal</span>
        </div>
        <v-btn block x-large rounded :color="splitColor" class="font-weight-bold black--text" @click="showCaloriesDialog = false"> Übernehmen </v-btn>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showDurationDialog" max-width="480" dark content-class="duration-dialog-overlay" :persistent="saveLocked || !!(liveSession && liveSession.timeReviewRequired)">
      <v-card class="duration-editor" role="document" aria-labelledby="duration-editor-title">
        <form novalidate @submit.prevent="applyDurationEdit">
          <div class="duration-editor-top">
            <span class="duration-editor-symbol"><v-icon color="#8ce7bb" size="26">mdi-timer-outline</v-icon></span>
            <v-btn icon :disabled="saveLocked" aria-label="Dauerprüfung schliessen" @click="closeDurationDialog"><v-icon size="22">mdi-close</v-icon></v-btn>
          </div>
          <div class="duration-editor-eyebrow">Dein Training</div>
          <h2 id="duration-editor-title">{{ liveSession && liveSession.timeReviewRequired ? 'Wie lange warst du aktiv?' : 'Trainingsdauer anpassen' }}</h2>
          <p id="duration-editor-hint" class="duration-editor-description">
            {{ liveSession && liveSession.timeReviewRequired
              ? editTiming && editTiming.durationSeconds >= 60
                ? 'Der Vorschlag reicht bis zu deinem letzten Eintrag. Passe die Dauer an, falls du länger trainiert hast.'
                : 'Aus deinen Einträgen lässt sich keine verlässliche Dauer ableiten. Bitte trage deine tatsächliche Trainingsdauer ein.'
              : 'Passe die Dauer und bei Bedarf das Datum deines Trainings an.' }}
          </p>
          <fieldset class="duration-fields" :disabled="saveLocked" aria-describedby="duration-editor-hint">
            <legend>Trainingsdauer</legend>
            <div class="duration-field-grid">
              <label class="duration-field" for="workout-duration-minutes">
                <input id="workout-duration-minutes" v-model="tempDurationMin" type="number" inputmode="numeric" min="0" max="1440" step="1" placeholder="0" :aria-invalid="!!durationEditError" />
                <span>Minuten</span>
              </label>
              <label class="duration-field" for="workout-duration-seconds">
                <input id="workout-duration-seconds" v-model="tempDurationSec" type="number" inputmode="numeric" min="0" max="59" step="1" placeholder="00" :aria-invalid="!!durationEditError" />
                <span>Sekunden</span>
              </label>
            </div>
          </fieldset>
          <div class="duration-preview" aria-live="polite">
            <span>{{ durationPreview ? 'Deine Trainingsdauer' : 'Dauer bitte eintragen' }}</span>
            <strong v-if="durationPreview">{{ durationPreview }}</strong>
          </div>

          <label class="duration-date" for="workout-duration-date">
            <span>Trainingsdatum</span>
            <input id="workout-duration-date" v-model="tempDateStr" type="date" :max="todayDate" :disabled="saveLocked" />
          </label>
          <v-alert v-if="durationEditError" dense text type="error" role="alert" class="mt-3 mb-0">{{ durationEditError }}</v-alert>
          <div class="duration-editor-actions">
            <v-btn type="submit" block height="52" depressed color="#8ce7bb" class="duration-confirm" :disabled="saveLocked">
              Dauer übernehmen<v-icon right size="20">mdi-check</v-icon>
            </v-btn>
            <v-btn block text height="44" :disabled="saveLocked" @click="closeDurationDialog">{{ liveSession && liveSession.timeReviewRequired ? 'Später prüfen' : 'Abbrechen' }}</v-btn>
          </div>
        </form>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showSaveFreeTrainingDialog" max-width="380" content-class="dialog-card">
      <v-card class="glass-modal pa-5">
        <div class="text-center mb-6">
          <v-avatar size="60" :color="splitColor + '33'" class="mb-3">
            <v-icon large :color="splitColor">mdi-content-save-edit</v-icon>
          </v-avatar>
          <div class="white--text font-weight-bold text-h6">Training sichern</div>
        </div>
        <v-card class="action-card mb-3" ripple @click="handleSaveFreeTraining(true)">
          <div class="d-flex align-center pa-3">
            <v-icon :color="splitColor" class="mr-3">mdi-playlist-plus</v-icon>
            <div>
              <div class="white--text font-weight-bold">Als Plan speichern</div>
              <div class="grey--text caption">Für die Zukunft merken</div>
            </div>
          </div>
        </v-card>
        <v-card class="action-card" ripple @click="handleSaveFreeTraining(false)">
          <div class="d-flex align-center pa-3">
            <v-icon color="white" class="mr-3">mdi-check</v-icon>
            <div>
              <div class="white--text font-weight-bold">Nur Session</div>
              <div class="grey--text caption">Einmaliges Workout</div>
            </div>
          </div>
        </v-card>
      </v-card>
    </v-dialog>

    <save-changes-dialog
      :dialog.sync="saveChangesDialogVisible"
      :modifications="getWorkoutModifications"
      @save-changes="handleSaveChanges"
      @discard-changes="handleDiscardChanges" />
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex"
import api from "@/api"
import moment from "moment"
import confetti from "canvas-confetti"
import SaveChangesDialog from "./saveChangesDialog.vue"
import AppleHealthWorkoutLink from "@/components/training/AppleHealthWorkoutLink.vue"
import watchSyncService from "@/services/watchSyncService"
import trainingFeatureAccess from "@/services/trainingFeatureAccess"
import trainingLiveService from "@/services/trainingLiveService"
import { store } from "@/store/stores"
import { workoutElapsedSeconds, workoutReviewTiming, workoutStartedAt, workoutDurationFields, workoutDurationFromFields, formatWorkoutDuration } from "@/services/workoutRecovery"
import { phoneCompanionWorkoutSource } from "@/services/watchPlatform"

export default {
  name: "WorkoutSummary",
  components: { SaveChangesDialog, AppleHealthWorkoutLink },
  data() {
    return {
      funFactIndex: 0,
      expandedSegmentIndex: null,
      trainingFeedback: "",
      isSaving: false,
      sessionSaved: Boolean(this.$store.state.runWorkoutStore.liveSession?.savedAt),
      selectedHealthWorkout: null,
      healthImportAvailable: false,
      summaryOpenedAt: Date.now(),
      saveChangesDialogVisible: false,
      showSaveFreeTrainingDialog: false,
      showCaloriesDialog: false,

      // Animation & Werte
      animatedVolume: 0,
      animatedSets: 0,
      animatedReps: 0,
      animatedDistance: 0,
      animatedCalories: 0,
      manualCalories: null,

      unixTimestamp: null,

      showDurationDialog: false,

      // Temp Werte für das Dialog-Feld
      tempDurationMin: 0,
      tempDurationSec: 0,
      tempDateStr: "",
      durationEditError: "",
      editTiming: null,
    }
  },
  computed: {
    canUseWatchFeature() { return trainingFeatureAccess.isAllowed() },
    saveLocked() { return this.isSaving || this.sessionSaved || Boolean(this.liveSession?.pendingSave) },
    ...mapState("runWorkoutStore", [
      "activeSplit",
      "originalActiveSplit",
      "completedSetsMap",
      "workoutStartTime",
      "hubGroupedExercises",
      "activePlanID",
      "hubGroupedSessions",
      "exerciseNotesMap",
      "watchCompanion",
      "liveSession",
    ]),
    ...mapGetters("runWorkoutStore", ["getWorkoutModifications"]),
    currentFunFact() {
      const vol = this.animatedVolume || 0
      const fact = this.funFactScale[this.funFactIndex]

      // Berechnung
      let countVal = (vol / fact.weight).toFixed(1)
      if (countVal.endsWith(".0")) countVal = countVal.replace(".0", "")

      // Tausender-Trennzeichen für riesige Hamster-Zahlen :)
      countVal = Number(countVal).toLocaleString("de-DE")

      return {
        ...fact,
        count: countVal,
      }
    },
    funFactScale() {
      return [
        { weight: 0.15, label: "Mäuse", singular: "Maus", icon: "mdi-rodent", color: "#BCAAA4" },
        { weight: 0.5, label: "Protein Drinks", singular: "Protein Drink", icon: "mdi-beer", color: "#FFD54F" },
        { weight: 4, label: "Hauskatzen", singular: "Katze", icon: "mdi-cat", color: "#FFB74D" },
        { weight: 70, label: "Waschmaschinen", singular: "Waschmaschine", icon: "mdi-washing-machine", color: "#90CAF9" },
        { weight: 180, label: "Löwen", singular: "Löwe", icon: "mdi-paw", color: "#FF8A65" },
        { weight: 700, label: "Kühe", singular: "Kuh", icon: "mdi-cow", color: "#A5D6A7" },
        { weight: 1200, label: "Kleinwagen", singular: "VW Golf", icon: "mdi-car-hatchback", color: "#9FA8DA" },
        { weight: 5000, label: "Elefanten", singular: "Elefant", icon: "mdi-elephant", color: "#CE93D8" },
        { weight: 180000, label: "Blauwale", singular: "Blauwal", icon: "mdi-fish", color: "#4FC3F7" },
      ]
    },
    // --- DESIGN ---
    splitColor() {
      return this.activeSplit?.fixedVisualization?.color || "#00E5FF"
    },
    splitTitle() {
      return this.activeSplit?.title || "Freies Training"
    },
    splitImage() {
      return this.activeSplit?.fixedVisualization?.img || ""
    },
    currentDate() {
      return moment(workoutStartedAt(this.$store.state.runWorkoutStore) || this.summaryOpenedAt).format("D. MMM YYYY")
    },
    todayDate() { return moment().format("YYYY-MM-DD") },

    // UPDATE: Dauer berücksichtigt manuelle Eingabe
    durationInSeconds() {
      return Math.floor(workoutElapsedSeconds(this.$store.state.runWorkoutStore, this.summaryOpenedAt))
    },
    healthImportExecution() {
      const start = workoutStartedAt(this.$store.state.runWorkoutStore) || this.summaryOpenedAt
      const end = this.liveSession?.endedAt || start + this.durationInSeconds * 1000
      return {
        title: this.splitTitle, duration: this.durationInSeconds,
        healthActivityFamily: this.isHyroxSummary ? "mixed" : this.totalReps > 0 ? "strength" : null,
        startedAt: new Date(start).toISOString(),
        endedAt: new Date(end).toISOString(), ts: Math.floor(start / 1000),
      }
    },

    // --- KALORIEN ---
    displayCalories() {
      return this.manualCalories !== null ? this.manualCalories : this.animatedCalories.toFixed(0)
    },
    calculatedCalories() {
      return (this.durationInSeconds / 60) * 7
    }, // ~7 kcal/min

    // --- ZEIT ---

    durationString() {
      return this.durationNeedsInput ? "Dauer offen" : formatWorkoutDuration(this.durationInSeconds)
    },
    durationNeedsInput() {
      return Boolean(this.liveSession?.timeReviewRequired && this.durationInSeconds < 60)
    },
    durationPreview() {
      const seconds = workoutDurationFromFields(this.tempDurationMin, this.tempDurationSec)
      return seconds === null ? "" : formatWorkoutDuration(seconds)
    },
    durationClock() {
      if (this.durationNeedsInput) return "Dauer offen"
      const s = this.durationInSeconds
      const h = Math.floor(s / 3600),
        m = Math.floor((s % 3600) / 60)
      const p = (n) => String(n).padStart(2, "0")
      return `${p(h)}:${p(m)}:${p(s % 60)}`
    },

    // --- STATS AGGREGATION ---
    actualCompletedSets() {
      return Object.values(this.completedSetsMap).flat()
        .filter((set) => Array.isArray(set) && set.length && !set.skipped && !set.some((item) => item?.skipped))
    },
    totalVolume() {
      return this.actualCompletedSets
        .reduce((acc, s) => {
          if (s.skipped) return acc
          const w = Number(s.find((i) => i.text === "Gewicht")?.value || 0)
          const r = Number(s.find((i) => /Wiederholungen/.test(i.text))?.value || 0)
          return acc + w * r
        }, 0)
    },
    totalSets() {
      return this.actualCompletedSets.length
    },
    totalReps() {
      return this.actualCompletedSets
        .reduce((acc, s) => {
          if (s.skipped) return acc
          return acc + Number(s.find((i) => /Wiederholungen/.test(i.text))?.value || 0)
        }, 0)
    },
    totalDistance() {
      return this.actualCompletedSets
        .reduce((acc, s) => {
          if (s.skipped) return acc
          return acc + Number(s.find((i) => i.text === "Distanz")?.value || 0)
        }, 0)
    },

    // --- TAGS ---
    focusTags() {
      if (!this.activeSplit?.exercises) return []
      const allTags = this.activeSplit.exercises.flatMap((ex) => {
        if (ex.isSuperset && Array.isArray(ex.exercises)) {
          return ex.exercises.flatMap((subEx) => subEx.tags?.map((tag) => tag.name) || [])
        }
        return ex.tags?.map((tag) => tag.name) || []
      })
      return [...new Set(allTags)].slice(0, 3)
    },

    // --- PERSONAL RECORDS (Vollständige Logik) ---
    newPersonalRecords() {
      if (!this.hubGroupedExercises || !this.completedSetsMap || !this.activeSplit) return []
      const records = []
      Object.entries(this.completedSetsMap).forEach(([exerciseIndex, sets]) => {
        if (!sets || sets.length === 0) return
        const exerciseDef = this.activeSplit.exercises[exerciseIndex]
        if (!exerciseDef) return
        const processExercise = (subExercise, subSets) => {
          const historicalData = this.hubGroupedExercises.find(
            (ex) => ex.exercise === subExercise._id || (ex.exercise && ex.exercise._id === subExercise._id),
          )
          if (!historicalData) return
          let currentMaxWeight = 0
          subSets.forEach((set) => {
            if (set.skipped) return
            const weight = Number(set.find((item) => item.text === "Gewicht")?.value || 0)
            if (weight > currentMaxWeight) currentMaxWeight = weight
          })
          const oldMaxWeight = historicalData.summary?.maxWeightStep?.weight || 0
          if (currentMaxWeight > oldMaxWeight) {
            records.push({
              exerciseTitle: subExercise.title,
              type: "Max Weight",
              oldValue: `${oldMaxWeight} kg`,
              newValue: `${currentMaxWeight} kg`,
            })
          }
        }
        if (exerciseDef.isSuperset) {
          const setsBySubExercise = sets.reduce((acc, set) => {
            if (set.subExerciseId) {
              if (!acc[set.subExerciseId]) acc[set.subExerciseId] = []
              acc[set.subExerciseId].push(set)
            }
            return acc
          }, {})
          Object.entries(setsBySubExercise).forEach(([subExerciseId, subSets]) => {
            const subExerciseDef = exerciseDef.exercises.find((ex) => ex._id === subExerciseId)
            if (subExerciseDef) processExercise(subExerciseDef, subSets)
          })
        } else {
          processExercise(exerciseDef, sets)
        }
      })
      return records
    },

    // --- HYROX LOGIK (Vollständig) ---
    isHyroxSummary() {
      return this.activeSplit?.isHyrox
    },
    previousHyroxRace() {
      if (!this.activeSplit || !this.hubGroupedSessions) return null
      const pastRaces = this.hubGroupedSessions.filter(
        (session) => session.execution && session.execution.title === this.activeSplit.title && session.execution.hyroxSegments,
      )
      return pastRaces.length > 0 ? pastRaces[0] : null
    },
    hyroxSegments() {
      if (!this.activeSplit?.isHyrox || !this.completedSetsMap) return []
      const previousRaceSegments = this.previousHyroxRace?.execution?.hyroxSegments || []
      return this.activeSplit.exercises
        .map((exercise, index) => {
          const completedSet = (this.completedSetsMap[index] || [])[0]
          if (!completedSet) return null
          const durationInSeconds = Number(completedSet.find((s) => s.text === "Dauer")?.value || 0)

          const formatTime = (seconds) => {
            if (isNaN(seconds) || seconds <= 0) return "---"
            const min = Math.floor(seconds / 60)
            const sec = Math.floor(seconds % 60)
            return `${String(min).padStart(2, "0")}:${String(sec).padStart(2, "0")}`
          }

          let comparison = null
          let comparisonClass = ""
          const previousSegment = previousRaceSegments.find((seg) => seg.segmentIndex === index)
          if (previousSegment && previousSegment.duration > 0 && durationInSeconds > 0) {
            const diff = durationInSeconds - previousSegment.duration
            const sign = diff <= 0 ? "-" : "+"
            comparisonClass = diff <= 0 ? "success--text" : "error--text"
            comparison = `${sign}${formatTime(Math.abs(diff))}`
          }
          return {
            title: exercise.title,
            exerciseId: exercise._id,
            time: formatTime(durationInSeconds),
            comparison,
            comparisonClass,
            icon: this.getImageUrl(exercise.picture || exercise.manual?.[0]?.picture),
            segmentIndex: index,
          }
        })
        .filter(Boolean)
    },
    hasExistingPlans() {
      return !!this.activePlanID
    },
  },
  watch: {
    canUseWatchFeature(allowed) { if (!allowed) { this.selectedHealthWorkout = null; this.healthImportAvailable = false } },
    workoutStartTime(value, previous) {
      if (value !== previous) { this.selectedHealthWorkout = null; this.summaryOpenedAt = Date.now() }
    },
    "liveSession.phase"(phase) { if (phase === "active") this.selectedHealthWorkout = null },
  },
  methods: {
    openHealthImport() { if (!this.saveLocked) this.$refs.healthImport?.openImport() },
    ...mapActions("runWorkoutStore", ["resetWorkoutState", "stopTimer"]),
    nextFunFact() {
      // Vibrations-Feedback für Haptik
      if (navigator.vibrate) navigator.vibrate(15)

      this.funFactIndex = (this.funFactIndex + 1) % this.funFactScale.length
    },

    // Intelligenter Start: Wähle beim Laden das passendste Tier
    setInitialFunFact() {
      const vol = this.totalVolume || 0
      // Finde den Index, wo das Gewicht am besten passt (Logic wie vorher)
      // Wir suchen das Tier, dessen Gewicht am nächsten an "vol / 2" ist oder einfach logisch passt
      // Einfache Logik: Wir nehmen das grösste Tier, das kleiner als das Gesamtvolumen ist
      let bestIndex = 0

      // Wir loopen durch und schauen, was Sinn macht (z.B. nicht 0.01 Elefanten)
      // Ziel: Mindestens 1.0 von dem Tier
      for (let i = 0; i < this.funFactScale.length; i++) {
        if (vol >= this.funFactScale[i].weight) {
          bestIndex = i
        }
      }
      this.funFactIndex = bestIndex
    },
    formatNumber(val) {
      return new Intl.NumberFormat("de-DE").format(Math.round(val))
    },
    openDurationDialog() {
      if (this.isSaving || this.sessionSaved || this.liveSession?.pendingSave) return
      this.editTiming = workoutReviewTiming(this.$store.state.runWorkoutStore)
      const fields = workoutDurationFields(this.editTiming.durationSeconds, this.liveSession?.timeReviewRequired)
      this.tempDurationMin = fields.minutes
      this.tempDurationSec = fields.seconds
      this.tempDateStr = moment(this.editTiming.startedAt || this.summaryOpenedAt).format("YYYY-MM-DD")
      this.durationEditError = ""
      this.showDurationDialog = true
    },

    closeDurationDialog() {
      if (this.saveLocked) return
      if (this.liveSession?.timeReviewRequired) this.leaveSummary()
      else this.showDurationDialog = false
    },

    async applyDurationEdit() {
      if (this.saveLocked) return
      const day = moment(this.tempDateStr, "YYYY-MM-DD", true)
      const seconds = workoutDurationFromFields(this.tempDurationMin, this.tempDurationSec)
      if (seconds === null) {
        this.durationEditError = "Bitte ganze Minuten und Sekunden von 0 bis 59 eintragen. Die Dauer muss grösser als 0 und höchstens 24 Stunden sein."
        return
      }
      if (!day.isValid()) {
        this.durationEditError = "Bitte ein gültiges Trainingsdatum wählen."
        return
      }
      const original = this.editTiming || {}
      const base = moment(original.startedAt || this.summaryOpenedAt)
      const start = day.clone().hour(base.hour()).minute(base.minute()).second(base.second()).millisecond(base.millisecond()).valueOf()
      // Preserve recorded pauses when only the date changes. Always adopt the visible duration in whole seconds.
      const unchangedDuration = Number.isFinite(original.durationSeconds) && seconds === Math.floor(original.durationSeconds)
      const elapsed = unchangedDuration && original.endedAt ? original.endedAt - original.startedAt : seconds * 1000
      const end = start + elapsed
      if (!trainingLiveService.confirmTiming({ startedAt: start, endedAt: end, durationSeconds: seconds })) {
        this.durationEditError = "Das Trainingsende darf nicht in der Zukunft liegen. Bitte Datum und Dauer prüfen."
        return
      }
      this.durationEditError = ""
      this.showDurationDialog = false
      this.animateStats()
      if (this.$route.query.resume === "true" && trainingLiveService.resumeRecovered()) {
        await this.$router.push({ name: "Workout_run" })
      }
    },
    leaveSummary() {
      if (this.isSaving) return
      if (this.liveSession?.recoveryReview || this.liveSession?.pendingSave || this.sessionSaved) {
        this.$router.push({ path: "/workout/hub", query: { mode: "own" } })
      } else {
        trainingLiveService.resume()
        this.$router.push({ name: "Workout_run" })
      }
    },
    // Helper
    isLightColor(c) {
      const hex = c.replace("#", "")
      const r = parseInt(hex.substr(0, 2), 16),
        g = parseInt(hex.substr(2, 2), 16),
        b = parseInt(hex.substr(4, 2), 16)
      return (r * 299 + g * 587 + b * 114) / 1000 > 155
    },
    getImageUrl(id) {
      if (!id) return "/img/default-exercise.webp"
      return `/img/uebungen/${String(id).replace(/\.[^/.]+$/, "")}.webp`
    },

    // Hyrox
    toggleSegment(index) {
      this.expandedSegmentIndex = this.expandedSegmentIndex === index ? null : index
    },
    getSegmentHistory(segment) {
      if (!this.hubGroupedSessions || !segment || !this.activeSplit) return []
      const history = []
      const pastRaces = this.hubGroupedSessions.filter(
        (session) => session.execution && session.execution.title === this.activeSplit.title && session.execution.hyroxSegments,
      )
      pastRaces.forEach((race) => {
        const pastSegment = race.execution.hyroxSegments.find((s) => s.segmentIndex === segment.segmentIndex)
        if (pastSegment && pastSegment.duration > 0) {
          const duration = Number(pastSegment.duration)
          const minutes = Math.floor(duration / 60)
          const seconds = Math.floor(duration % 60)
          history.push({
            date: moment(parseInt(race.execution.ts, 10) * 1000).format("DD.MM.YY"),
            time: `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`,
          })
        }
      })
      if (segment.time !== "---") history.unshift({ date: "Heute", time: segment.time })
      return history
    },

    // Speichern
    async saveSessionAndExit() {
      if (this.isSaving) return
      if (this.sessionSaved) return this.openSavedTraining()
      if (!this.activeSplit) return
      if (this.liveSession?.timeReviewRequired) { this.openDurationDialog(); return }
      if (this.liveSession?.pendingSave) return this.executeSave({ shouldSaveAsSplit: false, planId: this.activePlanID })
      if (this.activeSplit.isFreeTraining && !this.activeSplit.isHyrox) {
        if (this.hasExistingPlans) this.showSaveFreeTrainingDialog = true
        else await this.executeSave({ shouldSaveAsSplit: true, planId: null })
      } else {
        await this.executeSave({ shouldSaveAsSplit: false, planId: this.activePlanID })
      }
    },
    async handleSaveFreeTraining(saveAsSplit) {
      this.showSaveFreeTrainingDialog = false
      await this.executeSave({ shouldSaveAsSplit: saveAsSplit, planId: saveAsSplit ? null : this.activePlanID })
    },
    async executeSave({ shouldSaveAsSplit, planId }) {
      if (this.isSaving || this.sessionSaved) return
      if (this.liveSession?.timeReviewRequired) { this.openDurationDialog(); return }
      if (!trainingLiveService.ensureSession()) {
        this.$toast.error("Dieses Training gehört nicht zum angemeldeten Konto.")
        return
      }
      try {
        this.stopTimer()
      } catch (e) {
        console.log(e)
      }
      this.isSaving = true
      let finalPlanId = planId
      let saved
      try {
        if (this.liveSession?.pendingSave) {
          finalPlanId = this.liveSession.pendingSave.execution.workout_id
          saved = await api.workoutApi.saveWorkoutSession(this.liveSession.pendingSave)
        } else {
          if (shouldSaveAsSplit) {
            const splitToSave = { ...this.activeSplit }
            delete splitToSave.isFreeTraining
            const response = await api.workoutApi.addSplit({ workout_id: null, split: splitToSave })
            if (response?._id) finalPlanId = response._id
          }

          this.unixTimestamp = this.healthImportExecution.ts
          const isHyrox = this.activeSplit.isHyrox
          const finalCalories = this.manualCalories !== null ? this.manualCalories : this.calculatedCalories.toFixed(0)

          const executionData = {
            title: this.activeSplit.title,
            fixedVisualization: this.activeSplit.fixedVisualization,
            workout_id: finalPlanId,
            duration: this.durationInSeconds,
            startedAt: this.healthImportExecution.startedAt,
            endedAt: this.healthImportExecution.endedAt,
            records: this.newPersonalRecords,
            liftedWeight: this.totalVolume,
            totalDistance: this.totalDistance,
            caloriesBurned: finalCalories,
            feedBack: this.trainingFeedback,
            ts: this.unixTimestamp,
            clientSessionId: trainingLiveService.ensureSession()?.clientSessionId || this.watchCompanion.clientSessionId || undefined,
            source: this.watchCompanion.clientSessionId
              ? phoneCompanionWorkoutSource(this.watchCompanion.platform || undefined)
              : undefined,
          }

          let legacySets = []
          if (isHyrox) {
            executionData.hyroxSegments = this.activeSplit.exercises.map((exercise, index) => {
              const completedSet = (this.completedSetsMap[index] || []).find((set) => Array.isArray(set) && set.length && !set.skipped && !set.some((item) => item?.skipped))
              if (!completedSet) return null
              const duration = completedSet ? Number(completedSet.find((s) => s.text === "Dauer")?.value || 0) : 0
              const weight = completedSet ? Number(completedSet.find((s) => s.text === "Gewicht")?.value || 0) : 0
              const reps = completedSet ? Number(completedSet.find((s) => /Wiederholungen/.test(s.text))?.value || 0) : 0
              const distance = completedSet ? Number(completedSet.find((s) => s.text === "Distanz")?.value || 0) : 0
              return {
                segmentIndex: index,
                title: this.originalActiveSplit.exercises[index]?.title || exercise.title,
                duration: duration,
                exercise_id: exercise._id,
                weight: weight,
                reps: reps,
                distance: distance,
              }
            }).filter(Boolean)
          } else {
            legacySets = this.transformSetsForBackend(finalPlanId)
          }

          const payload = { execution: executionData, sets: legacySets }
          this.$store.commit("runWorkoutStore/SET_LIVE_SESSION", { pendingSave: JSON.parse(JSON.stringify(payload)) })
          saved = await api.workoutApi.saveWorkoutSession(payload)
        }
      } catch (error) {
        console.error(error)
        const isTimeout = ["ECONNABORTED", "ETIMEDOUT"].includes(error?.code)
        const message = isTimeout
          ? "Speichern nicht bestätigt. Die Anfrage dauert zu lange. Bitte erneut versuchen. Dein Training bleibt hier erhalten."
          : error?.response
            ? "Training konnte nicht vollständig gespeichert werden. Bitte erneut versuchen. Dein Training bleibt hier erhalten."
            : error?.request || error?.isAxiosError
              ? "Speichern nicht bestätigt. Bitte Verbindung prüfen und erneut versuchen. Dein Training bleibt hier erhalten."
              : "Training konnte in der App nicht zum Speichern vorbereitet werden. Dein Training bleibt hier erhalten."
        this.$toast.error(message, { duration: 10000 })
        this.isSaving = false
        return
      }

      // From here on the training is saved. Optional follow-ups must never invite
      // another session submission or wait for the native display queue.
      this.sessionSaved = true
      trainingLiveService.saved().catch((error) => console.warn("Training-Live-Anzeige konnte nicht beendet werden:", error))
      await Promise.all([this.updatePlanAfterSession(finalPlanId), this.linkHealthAfterSession(saved)])
      await this.openSavedTraining()
    },
    async openSavedTraining() {
      if (!this.sessionSaved) return
      this.isSaving = true
      try {
        await this.$router.push("/workout/hub")
        // Keep the summary intact until its destination has actually opened.
        this.resetWorkoutState().catch((error) => console.warn("Live-Workout konnte nicht beendet werden:", error))
      } catch (error) {
        console.warn("Training gespeichert, Trainingshub konnte nicht geöffnet werden:", error)
        this.$toast.warning("Training gespeichert. Die Übersicht konnte nicht geöffnet werden. Bitte tippe erneut auf Zur Trainingsübersicht.", { duration: 10000 })
      } finally {
        this.isSaving = false
      }
    },
    async updatePlanAfterSession(planId) {
      try {
        if (this.liveSession?.recoveryReview) return
        if (this.activeSplit.isFreeTraining) return
        const split = this.getUpdatedSplit([])
        if (JSON.stringify(split) === JSON.stringify(this.originalActiveSplit)) return
        await api.workoutApi.updateSplit({ workout_id: planId, split }, false, { timeout: 15000 })
      } catch (error) {
        console.warn("Training gespeichert, Planupdate fehlgeschlagen:", error)
        this.$toast.warning("Training gespeichert. Gewichte und Notizen konnten nicht in den Trainingsplan übernommen werden.", { duration: 10000 })
      }
    },
    async linkHealthAfterSession(saved) {
      if (!trainingFeatureAccess.isAllowed() || !this.selectedHealthWorkout) return
      const access = trainingFeatureAccess.capture()
      try {
        if (!saved?.executionId) throw new Error("Die Zuordnung ist nach der Server-Aktualisierung im Trainingshub möglich.")
        await api.workoutApi.linkHealthWorkout(saved.executionId, this.selectedHealthWorkout)
      } catch (error) {
        trainingFeatureAccess.reject(access, error)
        if (!trainingFeatureAccess.isCurrent(access)) return
        this.$toast.warning("Training gespeichert. Uhrdaten bitte im Trainingshub zuordnen: " + (error?.response?.data?.message || error.message || "Verbindung unterbrochen."), { duration: 10000 })
      }
    },
    transformSetsForBackend(freeTrainingWorkoutID = null) {
      const exerciseExecutions = []
      Object.entries(this.completedSetsMap).forEach(([exerciseIndex, performedSets]) => {
        if (!performedSets || performedSets.length === 0 || performedSets.every((s) => s.skipped)) return
        const exercise = this.activeSplit.exercises[exerciseIndex]
        if (!exercise) return
        const steps = performedSets
          .map((set, setIndex) => {
            if (!Array.isArray(set) || !set.length || set.skipped || set.some((item) => item?.skipped)) return null
            return {
              set: setIndex + 1,
              weight: Number(set.find((item) => item.text === "Gewicht")?.value || 0),
              reps: Number(set.find((item) => /Wiederholungen/.test(item.text))?.value || 0),
              distance: Number(set.find((item) => item.text === "Distanz")?.value || 0),
              time: set.find((item) => item.text === "Dauer")?.value || null,
              subExerciseId: set.subExerciseId || null, // FIX: SubExerciseId für Supersätze speichern!
              feedback: {
                difficulty: set.find((item) => item.text === "Difficulty")?.value,
                newWeightSuggestion: set.find((item) => item.text === "newWeight")?.value,
              },
            }
          })
          .filter(Boolean)
        if (steps.length > 0) {
          exerciseExecutions.push({
            exercise_id: exercise._id,
            split: this.activeSplit.title,
            workoutId: freeTrainingWorkoutID || this.activePlanID,
            timestamp: this.unixTimestamp,
            steps: steps,
          })
        }
      })
      return exerciseExecutions
    },
    getUpdatedSplit(modificationsToApply) {
      const splitCopy = JSON.parse(JSON.stringify(this.originalActiveSplit))

      // 1. Struktur-Anpassungen (Swap/Add/Delete) anwenden
      if (modificationsToApply && modificationsToApply.length > 0) {
        modificationsToApply.forEach((mod) => {
          if (mod.type === "swap") {
            const targetIndex = splitCopy.exercises.findIndex((ex) => ex._id === mod.payload.originalExercise._id)
            if (targetIndex > -1) splitCopy.exercises[targetIndex] = mod.payload.newExercise
          }
        })
        const deletions = modificationsToApply.filter((mod) => mod.type === "delete")
        deletions.forEach((mod) => {
          const targetIndex = splitCopy.exercises.findIndex((ex) => ex._id === mod.payload.exercise._id)
          if (targetIndex > -1) {
            splitCopy.exercises.splice(targetIndex, 1)
          }
        })
        const additions = modificationsToApply.filter((mod) => mod.type === "add").sort((a, b) => a.payload.atIndex - b.payload.atIndex)
        additions.forEach((mod) => splitCopy.exercises.splice(mod.payload.atIndex, 0, mod.payload.newExercise))
      }

      // 2. Gewichte aktualisieren
      Object.entries(this.completedSetsMap).forEach(([exerciseIndex, performedSets]) => {
        const performedExerciseId = this.activeSplit.exercises[exerciseIndex]?._id
        const exerciseToUpdateInCopy = splitCopy.exercises.find((ex) => ex._id === performedExerciseId)

        if (!exerciseToUpdateInCopy) return

        // === FALL A: SUPERSATZ ===
        if (exerciseToUpdateInCopy.isSuperset) {
          const subExerciseSetCounters = {}

          performedSets.forEach((performedSet) => {
            // Nur Sätze mit Sub-ID beachten
            if (performedSet.skipped || performedSet.isWarmup || !performedSet.subExerciseId) return

            const subId = performedSet.subExerciseId
            // Counter initialisieren
            if (subExerciseSetCounters[subId] === undefined) subExerciseSetCounters[subId] = 0

            const currentSetIndexForSub = subExerciseSetCounters[subId]

            // Die entsprechende Unter-Übung im Plan-Kopie finden
            const subExerciseInCopy = exerciseToUpdateInCopy.exercises.find((e) => e._id === subId)

            // Prüfen, ob im Plan ein Satz an dieser Stelle existiert
            if (subExerciseInCopy && subExerciseInCopy.sets && subExerciseInCopy.sets[currentSetIndexForSub]) {
              // Neue Werte holen
              const newWeightSuggestion = performedSet.find((item) => item.text === "newWeight")?.value
              const actualLiftedWeight = performedSet.find((item) => item.text === "Gewicht")?.value

              let finalWeightToSet = null
              if (newWeightSuggestion != null) finalWeightToSet = newWeightSuggestion
              else if (actualLiftedWeight != null) finalWeightToSet = actualLiftedWeight

              // Im Plan speichern
              if (finalWeightToSet !== null) {
                const weightSetting = subExerciseInCopy.sets[currentSetIndexForSub].find((s) => s.text === "Gewicht")
                if (weightSetting && Number(weightSetting.value) !== Number(finalWeightToSet)) {
                  weightSetting.value = finalWeightToSet
                }
              }
            }

            // Counter hochzählen (Skipped Sätze zählen im Plan-Index meist trotzdem mit,
            // es sei denn du willst übersprungene Sätze nicht als "erledigt" im Plan werten)
            if (!performedSet.skipped) {
              subExerciseSetCounters[subId]++
            }
          })

          // Supersatz ist hiermit erledigt, weiter zur nächsten Übung
          return
        }

        // === FALL B: NORMALE ÜBUNG ===
        const originalExercise = this.originalActiveSplit.exercises.find((ex) => ex._id === performedExerciseId)

        // Sicherheitscheck: Wenn normale Übung keine Sets hat, abbrechen
        if (!exerciseToUpdateInCopy.sets || !originalExercise || !originalExercise.sets) return

        const hasWarmup = originalExercise.warmup && originalExercise.warmup.length > 0
        let regularSetIndex = -1

        performedSets.forEach((performedSet, performedSetIndex) => {
          if (performedSet.skipped || performedSet.isWarmup || performedSet.some?.((item) => item?.skipped)) return
          if (hasWarmup && performedSetIndex === 0) return
          regularSetIndex++

          const newWeightSuggestion = performedSet.find((item) => item.text === "newWeight")?.value
          const actualLiftedWeight = performedSet.find((item) => item.text === "Gewicht")?.value

          let finalWeightToSet = null
          if (newWeightSuggestion != null) finalWeightToSet = newWeightSuggestion
          else if (actualLiftedWeight != null) finalWeightToSet = actualLiftedWeight

          if (finalWeightToSet !== null) {
            const setToUpdateInPlan = exerciseToUpdateInCopy.sets[regularSetIndex]
            if (setToUpdateInPlan) {
              const weightSettingInPlan = setToUpdateInPlan.find((s) => s.text === "Gewicht")
              if (weightSettingInPlan && Number(weightSettingInPlan.value) !== Number(finalWeightToSet)) weightSettingInPlan.value = finalWeightToSet
            }
          }
        })
      })

      // 3. Notizen aktualisieren
      Object.entries(this.exerciseNotesMap).forEach(([exerciseIndex, note]) => {
        const exerciseInActiveSplit = this.activeSplit.exercises[exerciseIndex]
        if (exerciseInActiveSplit) {
          const exerciseToUpdateInCopy = splitCopy.exercises.find((ex) => ex._id === exerciseInActiveSplit._id)
          if (exerciseToUpdateInCopy) this.$set(exerciseToUpdateInCopy, "userNote", note)
        }
      })

      return splitCopy
    },
    handleSaveChanges(mods) {
      this.saveChangesDialogVisible = false
      this.updatePlanInBackend(mods)
    },
    handleDiscardChanges() {
      this.saveChangesDialogVisible = false
    },
    async updatePlanInBackend(mods) {
      const updatedSplit = this.getUpdatedSplit(mods)
      await api.workoutApi.updateSplit({ workout_id: this.activePlanID, split: updatedSplit })
    },

    // Animation
    animateStats() {
      this.manualCalories = Math.floor(this.calculatedCalories)
      this.animateValue("animatedVolume", this.totalVolume, 1500)
      this.animateValue("animatedSets", this.totalSets, 1000)
      this.animateValue("animatedReps", this.totalReps, 1200)
      this.animateValue("animatedDistance", this.totalDistance, 1200)
      this.animateValue("animatedCalories", this.calculatedCalories, 1400)
    },
    animateValue(key, end, duration) {
      let start = null
      const startVal = 0
      const step = (ts) => {
        if (!start) start = ts
        const prog = Math.min((ts - start) / duration, 1)
        const ease = 1 - Math.pow(1 - prog, 3)
        this[key] = Math.floor(ease * (end - startVal) + startVal)
        if (prog < 1) requestAnimationFrame(step)
      }
      requestAnimationFrame(step)
    },
    triggerConfetti() {
      confetti({ particleCount: 150, spread: 70, origin: { y: 0.6 }, colors: [this.splitColor, "#fff"] })
    },
  },
  async beforeRouteEnter(to, from, next) {
    await store.restored
    next(trainingLiveService.ensureSession() ? undefined : { path: "/workout/hub", query: { mode: "own" } })
  },
  mounted() {
    trainingLiveService.ending()
    if (!this.liveSession?.pendingSave && !this.sessionSaved && (this.liveSession?.timeReviewRequired || workoutReviewTiming(this.$store.state.runWorkoutStore).durationSeconds === null)) {
      trainingLiveService.prepareRecovery()
      this.openDurationDialog()
    }
    if (this.watchCompanion.clientSessionId && !["ended", "stopping"].includes(this.watchCompanion.status)) {
      watchSyncService.stopPhoneCompanionWorkout().catch((error) => {
        this.$toast.warning(error?.message || "Watch-Pulstracking wird beendet, sobald die Watch erreichbar ist")
      })
    }
    this.animateStats()
    this.setInitialFunFact()
    setTimeout(this.triggerConfetti, 500)

    if (this.getWorkoutModifications.length > 0 && !this.activeSplit.isFreeTraining && !this.liveSession?.recoveryReview && !this.liveSession?.pendingSave && !this.sessionSaved) {
      setTimeout(() => {
        this.saveChangesDialogVisible = true
      }, 2000) // 2 Sekunden Verzögerung, damit man erst das Design geniessen kann
    }
  },
}
</script>

<style scoped>
/* --- MAIN LAYOUT & BG --- */
.summary-page {
  width: 100%;
  min-height: 100vh;
  background-color: #000;
  color: white;
  font-family: "Roboto", sans-serif;
  overflow-x: hidden;
  position: relative;
}

.fixed-background {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to bottom, #111 0%, #000 100%); /* Aufgehellter Background */
  z-index: 0;
  pointer-events: none;
}
.ambient-light {
  position: absolute;
  border-radius: 50%;
  filter: blur(90px);
  opacity: 0.35;
}
.main-spot {
  top: -20%;
  left: -20%;
  width: 100%;
  height: 60vh;
}
.secondary-spot {
  bottom: -10%;
  right: -20%;
  width: 80%;
  height: 40vh;
  opacity: 0.15;
}
.noise-texture {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E");
  z-index: 1;
}

/* --- HEADER --- */
.glass-header {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 8px;
  z-index: 100;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}
.header-content {
  text-align: center;
  line-height: 1.1;
}
.header-title {
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 1px;
}
.header-date {
  display: block;
  font-size: 0.7rem;
  color: #aaa;
}
.scroll-content {
  position: relative;
  z-index: 2;
  padding-top: 60px;
}
.health-summary-section { max-width: 1185px; margin: 0 auto; padding-bottom: 170px; }
.health-save-shortcut { position: relative; z-index: 1; display: flex; align-items: center; justify-content: center; gap: 7px; width: 100%; min-height: 44px; margin-bottom: 8px; color: #d5e5dc; font-size: 12px; }
.health-save-shortcut:focus-visible { outline: 2px solid #00963f; outline-offset: 3px; border-radius: 12px; }

/* --- COMPONENTS --- */
.hero-avatar {
  border-radius: 50%;
  width: 120px;
  height: 120px;
  margin: 0 auto;
  border: 4px solid;
  padding: 4px;
  background: #111;
  overflow: hidden;
}
.shadow-text {
  text-shadow: 0 10px 30px rgba(0, 0, 0, 0.8);
}

/* Glass Cards */
.glass-card {
  background: rgba(255, 255, 255, 0.07); /* Heller, besserer Kontrast */
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  overflow: hidden;
  position: relative;
  transition: transform 0.2s;
}
.full-width {
  grid-column: span 2;
}
.relative {
  position: relative;
}
.stat-hero {
  grid-column: span 2;
  min-height: 140px;
}
.bg-icon {
  position: absolute;
  right: -20px;
  bottom: -30px;
  opacity: 0.08;
  transform: rotate(-15deg);
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

/* Edit Circle */
.edit-circle {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
}
.ripple-effect:active {
  transform: scale(0.98);
}

/* Textareas (Fixed visibility) */
.bright-input >>> .v-input__slot {
  background: rgba(255, 255, 255, 0.12) !important;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.bright-input >>> input::placeholder,
.bright-input >>> textarea::placeholder {
  color: #ccc !important;
  opacity: 1;
}
.bright-input >>> textarea {
  color: white !important;
}

/* Footer */
.transparent-footer {
  background: transparent !important;
  z-index: 90;
  bottom: 0;
}
.footer-blur {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.95) 40%, transparent);
  pointer-events: none;
  z-index: -1;
}
.save-btn {
  letter-spacing: 1px;
}

/* Dialogs */
.summary-recovery {
  display: flex;
  align-items: center;
  gap: 14px;
  max-width: 1153px;
  width: calc(100% - 32px);
  margin: 22px auto 0;
  padding: 18px;
  border: 1px solid #3a5144;
  border-radius: 20px;
  background: linear-gradient(115deg, #24352b, #1b2620);
}
.summary-recovery-icon, .duration-editor-symbol {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border: 1px solid rgba(140, 231, 187, .16);
  border-radius: 14px;
  background: rgba(140, 231, 187, .09);
}
.summary-recovery-copy { display: flex; flex: 1; flex-direction: column; gap: 3px; min-width: 0; }
.summary-recovery-copy strong { color: #f3f8f5; font-size: 15px; line-height: 1.4; }
.summary-recovery-copy span { color: #b5c7bc; font-size: 12px; line-height: 1.5; }
.summary-recovery .summary-recovery-action { color: #a8efca; font-weight: 700; text-transform: none; letter-spacing: 0; }
.duration-stat { width: 100%; min-width: 0; align-items: center; min-height: 115px; }
.duration-stat-value { max-width: 100%; color: #f3f8f5; font-size: clamp(18px, 3.8vw, 25px); font-weight: 750; line-height: 1.35; font-variant-numeric: tabular-nums; }
.duration-stat-hint { margin-top: 5px; color: #b0c8ba; font-size: 11px; line-height: 1.5; }
.duration-stat .edit-circle { top: 9px; right: 9px; width: 22px; height: 22px; }
.duration-stat:focus-visible, .summary-recovery .v-btn:focus-visible, .duration-editor .v-btn:focus-visible { outline: 3px solid #8ce7bb; outline-offset: 3px; }
.duration-stat:disabled { cursor: default; }
.duration-editor {
  padding: 24px;
  color: #f3f8f5;
  border: 1px solid #35403a;
  border-radius: 24px !important;
  background: linear-gradient(155deg, #222d27, #191e1b 55%) !important;
}
.duration-editor-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
.duration-editor-top .v-btn { color: #b4c1b9; }
.duration-editor-eyebrow { color: #a8cfb9; font-size: 11px; letter-spacing: .09em; line-height: 1.5; font-weight: 700; text-transform: uppercase; }
.duration-editor h2 { margin: 6px 0 10px; font-size: 25px; font-weight: 750; line-height: 1.25; letter-spacing: -.025em; }
.duration-editor-description { margin-bottom: 20px; color: #b5c2b9; font-size: 14px; line-height: 1.6; }
.duration-fields { min-width: 0; margin: 0; padding: 0; border: 0; }
.duration-fields legend { padding: 0; margin-bottom: 8px; color: #d0ddd5; font-size: 13px; font-weight: 600; }
.duration-field-grid { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 12px; }
.duration-field {
  display: flex;
  min-width: 0;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  padding: 13px 12px;
  border: 1px solid #46554b;
  border-radius: 16px;
  background: #131a16;
  cursor: text;
}
.duration-field:focus-within { border-color: #8ce7bb; box-shadow: 0 0 0 2px rgba(140, 231, 187, .16); }
.duration-field input {
  min-width: 0;
  width: 100%;
  padding: 0;
  color: #f3f8f5;
  background: transparent;
  font-size: 38px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  text-align: center;
  line-height: 1.25;
  outline: none;
  appearance: textfield;
  -moz-appearance: textfield;
}
.duration-field input::-webkit-inner-spin-button, .duration-field input::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
.duration-field input::placeholder { color: #6d8274; opacity: 1; }
.duration-field span { color: #aabeb1; font-size: 13px; }
.duration-preview { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 4px 12px; min-height: 21px; margin-top: 10px; font-size: 12px; line-height: 1.6; color: #a8bbb0; }
.duration-preview strong { color: #a8efca; font-weight: 650; }
.duration-date { display: flex; flex-direction: column; gap: 8px; margin-top: 20px; color: #d0ddd5; font-size: 13px; font-weight: 600; }
.duration-date input { width: 100%; min-width: 0; min-height: 48px; padding: 11px 14px; border: 1px solid #46554b; border-radius: 13px; color: #f3f8f5; background: #131a16; font: inherit; font-size: 16px; color-scheme: dark; }
.duration-date input:focus-visible { outline: 2px solid #8ce7bb; outline-offset: 2px; }
.duration-editor-actions { display: grid; gap: 6px; margin-top: 22px; }
.duration-editor-actions .v-btn { border-radius: 13px; color: #b4c1b9; font-size: 14px; font-weight: 650; text-transform: none; letter-spacing: 0; }
.duration-editor-actions .duration-confirm { color: #13251c !important; }
@media (prefers-reduced-motion: reduce) {
  .duration-stat { transition: none; }
  .duration-stat:active { transform: none; }
}
@media (max-width: 480px) {
  .summary-recovery { flex-wrap: wrap; gap: 10px; padding: 16px; }
  .summary-recovery-icon { align-self: flex-start; width: 36px; height: 36px; border-radius: 12px; }
  .summary-recovery-copy { flex-basis: calc(100% - 46px); }
  .summary-recovery .summary-recovery-action { margin-left: 36px; height: 44px; }
  .duration-editor { padding: 20px; }
  .duration-editor h2 { font-size: 24px; }
  .duration-field input { font-size: 34px; }
}

.dialog-card {
  box-shadow: none !important;
  overflow: visible !important;
}
.glass-modal {
  background: #1e1e1e !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 24px !important;
}
.input-display {
  background: rgba(0, 0, 0, 0.3);
  border-radius: 16px;
  padding: 12px;
  display: flex;
  justify-content: center;
  align-items: baseline;
}
.big-input {
  background: transparent;
  border: none;
  outline: none;
  font-size: 2.5rem;
  font-weight: 800;
  color: white;
  text-align: right;
  width: 100px;
}
.unit {
  color: #888;
  font-weight: bold;
  margin-left: 8px;
}
.action-card {
  background: rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  cursor: pointer;
}

/* PR Slider */
.pr-card {
  min-width: 170px;
  background: linear-gradient(145deg, #222, #111);
}
.section-title {
  font-size: 0.8rem;
  font-weight: 900;
  color: #888;
  letter-spacing: 1px;
}

/* Hyrox */
.hyrox-title {
  font-family: "Arial Black", sans-serif;
  font-size: 2.5rem;
  line-height: 1;
  margin-bottom: 8px;
}
.hyrox-time {
  font-size: 3rem;
  font-weight: bold;
  color: white;
  line-height: 1;
  margin-bottom: 8px;
}
.glass-list-item {
  background: rgba(255, 255, 255, 0.08);
  border-radius: 12px;
}
.history-bg {
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.05);
}
.glass-card {
  background: rgba(30, 30, 30, 0.6) !important;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  border-radius: 20px !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.glow-bg {
  position: absolute;
  top: -50%;
  right: -50%;
  width: 100%;
  height: 200%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.03) 0%, transparent 70%);
  transform: rotate(-15deg);
  pointer-events: none;
}

.fun-fact-icon-wrapper {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid; /* Farbe kommt inline */
  display: flex;
  align-items: center;
  justify-content: center;
}

.fun-fact-icon {
  filter: drop-shadow(0 0 10px currentColor);
}

@keyframes float-icon {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
}

.tap-hint-badge {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  padding: 4px 8px;

  font-size: 0.6rem;
  font-weight: 800;
  letter-spacing: 1px;
  color: white;

  display: flex;
  align-items: center;

  /* Die Puls-Animation */
  animation: gentle-pulse 2s infinite ease-in-out;
  pointer-events: none; /* Klick geht durch auf die Karte */
}

@keyframes gentle-pulse {
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.1);
    background: rgba(255, 255, 255, 0.1);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 0 4px rgba(255, 255, 255, 0);
    background: rgba(255, 255, 255, 0.2);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
    background: rgba(255, 255, 255, 0.1);
  }
}
</style>

<style>
.v-dialog.duration-dialog-overlay { margin: 16px; width: calc(100% - 32px); border-radius: 24px; }
</style>
